import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PaymentAPI } from "../infrastructure/api/Payment.api";
import {
    PaymentCreateInput,
    PaymentFilters,
    PaymentSendInput,
    PaymentUpdateInput,
} from "../domain/types";

const PAYMENTS_KEY = "payments";

export const usePayments = (filters: PaymentFilters = {}) =>
    useQuery({
        queryKey: [PAYMENTS_KEY, filters],
        queryFn: async () => {
            const { data } = await PaymentAPI.list(filters);
            return data;
        },
    });

/**
 * Who charges this patient — the therapist or their centre — and from which
 * defaults. Asked per patient because a therapist in a centre has both kinds.
 */
export const usePaymentAccount = (patientId: string | undefined) =>
    useQuery({
        queryKey: [PAYMENTS_KEY, "account", patientId],
        queryFn: async () => {
            const { data } = await PaymentAPI.account(patientId as string);
            return data;
        },
        enabled: Boolean(patientId),
    });

/**
 * The chosen patient's sessions, for the "which session is this for" picker.
 * Only that patient's: a payment is owed by one person, so offering the whole
 * caseload's appointments would offer mistakes.
 */
export const usePayableSessions = (patientId: string) =>
    useQuery({
        queryKey: ["sessions-for-payment", patientId],
        queryFn: async () => {
            const { data } = await PaymentAPI.sessionsOf(patientId, { limit: 50 });
            return data;
        },
        enabled: Boolean(patientId),
    });

export const usePayment = (id: string | undefined) =>
    useQuery({
        queryKey: [PAYMENTS_KEY, "detail", id],
        queryFn: async () => {
            const { data } = await PaymentAPI.detail(id as string);
            return data;
        },
        enabled: Boolean(id),
    });

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: [PAYMENTS_KEY] });
};

export const useCreatePayment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: PaymentCreateInput) => {
            const { data } = await PaymentAPI.create(payload);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useUpdatePayment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            payload,
        }: {
            id: string;
            payload: PaymentUpdateInput;
        }) => {
            const { data } = await PaymentAPI.update(id, payload);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useSendPayment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            payload,
        }: {
            id: string;
            payload: PaymentSendInput;
        }) => {
            const { data } = await PaymentAPI.send(id, payload);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useMarkPaymentPaid = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await PaymentAPI.markPaid(id);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useSendPaymentReminder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await PaymentAPI.remind(id);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useCancelPayment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await PaymentAPI.cancel(id);
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};
